<template>
  <div class="date-range-filter">
    <button class="date-range-filter__trigger button" :disabled="isReportLoading" @click="toggleDateSelector">
      <div>
        <font-awesome-icon class="date-range-filter__trigger-icon" icon="calendar-alt"></font-awesome-icon>
      </div>
      <div>
        <span>{{ selectedDateOption.label }}</span>
      </div>
    </button>
    <div class="date-range-filter__dropdown" v-if="dateSelectorOpen" v-click-away="cancel">
      <div class="date-range-filter__dropdown-content">
        <div class="columns">
          <div class="column is-narrow date-range-filter__presets">
            <div>
              <div v-for="(date, index) in dateOptions" :key="date.label" class="date-range-filter__preset-container">
                <hr v-if="shouldDisplayPresetDivider(index)" />
                <a
                  :key="date.label"
                  @click="setDateOption(date)"
                  class="date-range-filter__preset"
                  :class="{ 'date-range-filter__preset--active': isActivePreset(date.label) }"
                  >{{ date.label }}</a
                >
              </div>
            </div>
          </div>
          <div class="column is-flex is-flex-direction-column is-justify-content-space-between">
            <div class="date-range-filter__date-picker">
              <vc-date-picker
                v-model.range="selectedDateRange"
                ref="calendar"
                :columns="2"
                range
                borderless
                is-required
                transparent
                :model-config="datePickerModelConfig"
                timezone="UTC"
                :attributes="datePickerAttributes"
                :available-dates="availableDates"
                @update:modelValue="handleDateChange"
              ></vc-date-picker>
            </div>
            <div class="date-range-filter__actions">
              <n-button-group>
                <n-button @click="applySelection" variant="primary">Apply</n-button>
                <n-button @click="cancel" variant="primary--outline">Cancel</n-button>
              </n-button-group>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script lang="ts" setup>
import { DatePicker, FluentDate } from '@/features/reports/services';
import { SET_REPORTING_DATE_RANGE } from '@/store/reports/mutationTypes';
import { DateGrouping, DateOption } from '@/features/reports/models';
import moment from 'moment';
import { useStore } from 'vuex';
import { computed, onMounted, ref, watch } from 'vue';
// Remove this line: import VcDatePicker from 'v-calendar';

const store = useStore();
const selectedDateOption = computed<DateOption>(() => store.getters.selectedDateOption);
const isReportLoading = computed<boolean>(() => store.getters.isReportLoading);
const setReportingDateRange = (dateOption: DateOption) => store.commit(SET_REPORTING_DATE_RANGE, dateOption);

// Use a generic Ref type or infer from the component instance
const calendar = ref<InstanceType<any> | null>(null); // Fallback type; refine if needed

const dateSelectorOpen = ref(false);
const value = ref<DateOption>(DatePicker.options.today);
const selectedDateRange = ref(DatePicker.options.today.getDateRange());

const datePickerModelConfig = ref({
  start: {
    timeAdjust: '00:00:00',
  },
  end: {
    timeAdjust: '23:59:59',
  },
});

const availableDates = {
  start: new FluentDate(new Date(2018, 0, 1)).startOfYear().value,
  end: new FluentDate().endOfYear().value,
};

const datePickerAttributes = [
  {
    key: 'today',
    highlight: {
      fillMode: 'outline',
    },
    dates: new Date(),
  },
];

onMounted(() => {
  if (!isSelectedDateInOptions(value.value)) {
    const val = getSelectedPreset() ?? getDateOptionForCustomRange();
    setReportingDateRange(val);
  }
});

watch(
  selectedDateOption,
  () => {
    value.value = selectedDateOption.value;
    selectedDateRange.value = selectedDateOption.value.getDateRange();
    moveCalendar();
  },
  { immediate: true },
);

function handleDateChange(changedValue: any): void {
  const selectedPreset = getSelectedPreset();
  if (selectedPreset) {
    value.value = selectedPreset;
    return;
  }
  value.value = getDateOptionForCustomRange();
}

function isSelectedDateInOptions(value: DateOption) {
  return dateOptions.value.some((option) => option.label === value.label);
}

const dateOptions = computed(() => DatePicker.options.toArray());

function getSelectedPreset(): DateOption | undefined {
  const matchingValues = DatePicker.options.toArray().filter((opt: DateOption) => {
    const optRange = opt.getDateRange();
    return (
      moment(selectedDateRange.value.start).isSame(optRange.start, 'day') &&
      moment(selectedDateRange.value.end).isSame(optRange.end, 'day')
    );
  });
  const matchingValueWithLabel = matchingValues.find((opt: DateOption) => {
    return opt.label === value.value.label;
  });
  return matchingValueWithLabel ? matchingValueWithLabel : matchingValues[0];
}

function getDateOptionForCustomRange(): DateOption {
  const selectedStart = selectedDateRange.value.start;
  const selectedEnd = selectedDateRange.value.end;
  return {
    group: getGroupingForCustomRange(),
    getDateRange: () => ({
      start: selectedStart,
      end: selectedEnd,
    }),
    label: generateLabelForCustomRange(),
    listGroup: 0,
  };
}

function cancel(): void {
  value.value = selectedDateOption.value;
  selectedDateRange.value = selectedDateOption.value.getDateRange();
  dateSelectorOpen.value = false;
}

function toggleDateSelector(): void {
  if (dateSelectorOpen.value) {
    cancel();
  } else {
    dateSelectorOpen.value = true;
  }
}

function isActivePreset(label?: string): boolean {
  if (!label) {
    return !DatePicker.options.toArray().some((opt: DateOption) => opt.label === value.value.label);
  }
  return value.value.label === label;
}

function setDateOption(dateRange: DateOption): void {
  if (calendar.value) {
    calendar.value.dragValue = null;
  }
  value.value = dateRange;
  selectedDateRange.value = dateRange.getDateRange();
  moveCalendar();
}

function applySelection(): void {
  setReportingDateRange(value.value);
  toggleDateSelector();
}

function shouldDisplayPresetDivider(index: number): boolean {
  if (index === 0) {
    return false;
  }
  return dateOptions.value[index].listGroup > dateOptions.value[index - 1].listGroup;
}

function getDaysBetweenDates(date1: Date, date2: Date): number {
  const oneDay = 24 * 60 * 60 * 1000;
  return Math.round(Math.abs((date1.getTime() - date2.getTime()) / oneDay));
}

function generateLabelForCustomRange(): string {
  if (startDateEqualsEndDate()) {
    return moment(selectedDateRange.value.start).utc().format('MMM D, YYYY');
  }
  return `${moment(selectedDateRange.value.start).utc().format('MMM D, YYYY')} - ${moment(selectedDateRange.value.end)
    .utc()
    .format('MMM D, YYYY')}`;
}

function startDateEqualsEndDate() {
  return getDaysBetweenDates(selectedDateRange.value.start, selectedDateRange.value.end) <= 1;
}

function moveCalendar(): void {
  selectedDateRange.value = value.value.getDateRange();
  if (calendar.value) {
    calendar.value.move(selectedDateRange.value.start, {
      position: 1,
      transition: 'slide-h',
    });
  }
}

function getGroupingForCustomRange(): DateGrouping {
  const daySpan = getDaysBetweenDates(selectedDateRange.value.start, selectedDateRange.value.end);
  if (daySpan === 1) {
    return DateGrouping.HOUR;
  }
  return daySpan < 91 ? DateGrouping.DAY : DateGrouping.MONTH;
}
</script>

<style lang="scss" scoped>
.date-range-filter {
  width: auto;
  display: inline-block;
  height: 40px;
  position: relative;
  &__trigger {
    background: #fff;
    border-radius: 4px;
    box-shadow: 0 2px 4px 0 rgba(0, 0, 0, 0.1);
    display: flex;
    padding-right: 2.5em;
    min-width: 180px;
    justify-content: flex-start;
    align-items: center;
    &::after {
      border: 3px solid transparent;
      border-radius: 2px;
      border-right: 0;
      border-top: 0;
      content: ' ';
      display: block;
      height: 0.625em;
      margin-top: -0.4375em;
      pointer-events: none;
      position: absolute;
      top: 50%;
      transform: rotate(-45deg);
      transform-origin: center;
      width: 0.625em;
      border-color: #485fc7;
      right: 1.125em;
      z-index: 4;
    }
  }
  &__trigger-icon {
    color: #dbdbdb;
    height: 1em;
    pointer-events: none;
    top: 0;
    width: 0.875em;
    z-index: 4;
    margin-right: 10px;
  }
  &__dropdown {
    left: 0;
    min-width: 12rem;
    padding-top: 4px;
    position: absolute;
    top: 100%;
    z-index: 20;
    left: auto;
    right: 0;
  }
  &__dropdown-content {
    background-color: #fff;
    border-radius: 4px;
    box-shadow:
      0 0.5em 1em -0.125em rgb(10 10 10 / 10%),
      0 0 0 1px rgb(10 10 10 / 2%);
    padding: 1rem 2rem;
    overflow: hidden;
  }
  &__presets {
    border-right: 1px solid #dbdbdb;
    padding-right: 2rem;
  }
  &__preset-container {
    hr {
      margin: 0.25rem 0;
    }
  }
  &__preset {
    color: $color-netcall-body-text;
    border: none;
    text-align: left;
    display: block;
    padding: 0.25rem 0;
    &:hover {
      color: #363636;
    }
    &--active {
      font-weight: bold;
      color: $primary;
    }
  }
  &__actions {
    display: flex;
    justify-content: flex-end;
  }
  :deep(.vc-container) {
    color: $color-netcall-body-text;
    font-family: inherit;
    --gray-200: #edf2f7;
    --gray-500: rgba($color-netcall-body-text, 0.8);
    --gray-800: #363636;
    --gray-900: $color-netcall-body-text;
    .vc-arrow {
      background-color: #fff;
      &:hover {
        background: var(--gray-200);
      }
    }
    .vc-title {
      font-size: 18px;
      background-color: #fff;
    }
  }
}
</style>
